/**
 * Out-of-office approval delegation — the WRITE side of a feature whose READ side already ships:
 * `apps/api/src/lib/org-chart.ts`'s `activeDelegationFor` already resolves a live
 * `ApprovalDelegation` row and `@hrms/workflow`'s `resolveApprover` already redirects a pending step
 * to it. This contract only lets a caller create and clear their own row.
 *
 * WORKFLOW-ENGINE-GENERIC, NOT LEAVE-SPECIFIC. `entityType` names any workflow entity type this org
 * uses ('leave_request' today, more later) — null means every one of them. Deliberately its own
 * contract rather than folded into `leave.contract.ts`.
 *
 * ALWAYS SELF-SCOPED. `fromPersonId` is never accepted from the caller — every route implementation
 * sets it to `actor.personId`, the same "being the subject IS the authorization" shape
 * `notifications.contract.ts` documents for its own inbox.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { errorBodySchema, instantSchema, uuidSchema } from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

export const delegationSchema = z.object({
  id: uuidSchema,
  fromPersonId: uuidSchema,
  toPersonId: uuidSchema,
  /** Resolved display name, same convention as every other DTO that carries one. */
  toPersonName: z.string(),
  /** Null means every entity type this org's workflows use. */
  entityType: z.string().nullable(),
  validFrom: instantSchema,
  validTo: instantSchema,
  createdAt: instantSchema,
});
export type Delegation = z.infer<typeof delegationSchema>;

export const createDelegationBodySchema = z
  .object({
    toPersonId: uuidSchema,
    entityType: z.string().nullable().default(null),
    validFrom: instantSchema,
    validTo: instantSchema,
  })
  .refine((body) => body.validTo > body.validFrom, {
    message: 'validTo must be after validFrom',
    path: ['validTo'],
  });

export const delegationContract = c.router(
  {
    createDelegation: {
      method: 'POST',
      path: '/',
      body: createDelegationBodySchema,
      responses: {
        201: delegationSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary: "Delegate the caller's own approvals to a colleague for a date range",
      metadata: routeMeta('workflow.delegate:manage'),
    },

    listDelegations: {
      method: 'GET',
      path: '/',
      responses: {
        200: z.array(delegationSchema),
        401: errorBodySchema,
      },
      summary: "The caller's own delegations, past and active, newest first",
      metadata: routeMeta('workflow.delegate:manage'),
    },

    deleteDelegation: {
      method: 'DELETE',
      path: '/:delegationId',
      responses: {
        200: delegationSchema,
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "Clear one of the caller's own delegations",
      metadata: routeMeta('workflow.delegate:manage'),
    },
  },
  { pathPrefix: '/delegations' },
);
